import React, { useEffect } from 'react';
import { Input } from '../../ui/input';
import ImageUploadField from '../../ImageUploadField';
import { useTranslation } from '../../../lib/i18n';
import { profileAPI } from '../../../lib/api';
import { toast } from 'sonner';
import { User, Building2, Phone } from 'lucide-react';

export default function WelcomeStep({ data, onChange }) {
  const { t } = useTranslation();

  // Load existing profile on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await profileAPI.get();
        const p = res.data;
        onChange({
          name: p.name || '',
          brand_name: p.brand_name || '',
          phone_number: p.phone_number || '',
          profile_image: p.profile_image || '',
          company_logo: p.company_logo || '',
        });
      } catch (err) {
        // 404 = new user with no profile yet (expected)
        // 401 = handled by global interceptor
        if (err.response?.status && err.response.status !== 404 && err.response.status !== 401) {
          toast.error(t('onboarding.profileLoadFailed') || 'Could not load profile');
        }
      }
    };
    loadProfile();
  }, []);

  // Auto-save profile when data changes (debounced)
  useEffect(() => {
    if (!data.name && !data.brand_name) return;
    const timer = setTimeout(async () => {
      try {
        await profileAPI.update({
          name: data.name,
          brand_name: data.brand_name,
          phone_number: data.phone_number,
          profile_image: data.profile_image,
          company_logo: data.company_logo,
        });
      } catch (err) {
        if (err.response?.status !== 401) {
          toast.error(t('onboarding.profileSaveFailed') || 'Could not save profile');
        }
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [data.name, data.brand_name, data.phone_number, data.profile_image, data.company_logo]);

  const update = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex p-4 bg-[#e9efe7] rounded-2xl mb-4">
          <User className="w-10 h-10 text-[#1f4d3a]" />
        </div>
        <h2 className="text-2xl font-bold text-[#23211a] mb-2">
          {t('onboarding.step1.title')}
        </h2>
        <p className="text-[#6f6a5c]">
          {t('onboarding.step1.subtitle')}
        </p>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-sm font-medium text-[#23211a] mb-1.5 block flex items-center gap-1.5">
            <User className="w-3.5 h-3.5 text-[#1f4d3a]" />
            {t('onboarding.step1.name')} *
          </label>
          <Input
            type="text"
            placeholder={t('onboarding.step1.namePlaceholder')}
            value={data.name}
            onChange={(e) => update('name', e.target.value)}
            className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a]"
            data-testid="onboarding-name"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-[#23211a] mb-1.5 block flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5 text-[#1f4d3a]" />
            {t('onboarding.step1.brandName')} *
          </label>
          <Input
            type="text"
            placeholder={t('onboarding.step1.brandPlaceholder')}
            value={data.brand_name}
            onChange={(e) => update('brand_name', e.target.value)}
            className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a]"
            data-testid="onboarding-brand"
          />
        </div>

        <div className="md:col-span-2">
          <label className="text-sm font-medium text-[#23211a] mb-1.5 block flex items-center gap-1.5">
            <Phone className="w-3.5 h-3.5 text-[#1f4d3a]" />
            {t('onboarding.step1.phone')}
          </label>
          <Input
            type="text"
            placeholder={t('onboarding.step1.phonePlaceholder')}
            value={data.phone_number}
            onChange={(e) => update('phone_number', e.target.value)}
            className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a] max-w-md"
            data-testid="onboarding-phone"
          />
        </div>
      </div>

      {/* Image Uploads. Optional but recommended.
          User feedback (after a few days of dogfooding): empty photo
          field caused renders to use a placeholder face from a stale
          template default, and users weren't aware the field would be
          consequential. The treatment below makes the recommendation
          visible without blocking progression. The heading says
          "recommended" (not "optional"), the photo helper text
          spells out the consequence ("ohne Foto nutzen wir einen
          Platzhalter"), and an inline badge calls out the photo field
          specifically since that's the highest-impact one. */}
      <div>
        <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
          <p className="text-sm font-medium text-[#2e6b50]">
            {t('onboarding.step1.optionalHeading') || 'Für persönlichere Videos (empfohlen)'}
          </p>
          <span className="text-[11px] text-[#8a8474] max-w-xs text-right">
            {t('onboarding.step1.optionalHint') || 'Ohne diese Daten nutzen wir Platzhalter im Abspann.'}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-medium text-[#23211a] mb-1 flex items-center gap-1.5 flex-wrap">
              {t('onboarding.step1.photo')}
              {!data.profile_image && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#dfe8db] text-[#2e6b50] border border-[#1f4d3a]/30 font-normal normal-case tracking-normal">
                  {t('onboarding.step1.photoRecommended') || 'Empfohlen. Sonst Platzhalter'}
                </span>
              )}
            </p>
            <p className="text-xs text-[#8a8474] mb-2">{t('onboarding.step1.photoDesc')}</p>
            <ImageUploadField
              value={data.profile_image}
              onChange={(url) => update('profile_image', url)}
              uploadEndpoint="profile/upload/photo"
              previewShape="rounded"
              testId="onboarding-photo"
              compact
              cropToSquare
            />
          </div>

          <div>
            <p className="text-sm font-medium text-[#23211a] mb-1">
              {t('onboarding.step1.logo')}
              <span className="ml-1.5 text-[10px] uppercase tracking-wider text-[#8a8474] font-normal">
                {t('onboarding.step1.optionalTag') || 'optional'}
              </span>
            </p>
            <p className="text-xs text-[#8a8474] mb-2">{t('onboarding.step1.logoDesc')}</p>
            <ImageUploadField
              value={data.company_logo}
              onChange={(url) => update('company_logo', url)}
              uploadEndpoint="profile/upload/logo"
              previewShape="square"
              testId="onboarding-logo"
              compact
            />
          </div>
        </div>
      </div>
    </div>
  );
}
